import React from 'react'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import type { Value as PhoneValue } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { LocationInput } from '../../../components/ui'
import { Skeleton } from '../../../components/ui/Skeleton'
import { PageShell } from './PageShell'
import { SettingsTabButtonIcon } from '../components/SettingsTabButtonIcon'
import { useDashboard } from '../DashboardContext'
import { formatLiveSince } from '../utils'
import { apiGet, apiPut } from '../../../lib/api'
import { BUSINESS, BUSINESS_LOGO, BUSINESS_TIMEZONES, BUSINESS_SCHEDULER_SLOTS } from '../../../lib/endpoints'
import { uploadToCloudinary } from '../../../lib/cloudinaryUpload'
import { ImageCropModal } from '../../../components/ui/ImageCropModal'
import type { BusinessInfo } from '../../../types'

type BusinessTabId = 'tab-profile' | 'tab-nudge' | 'tab-voice' | 'tab-schedule' | 'tab-email'

type SchedulerSlotResponse = {
  slotId: string
  timezone: string
  slotCode: string
  fireTimeLocal: string
  active: boolean
}

type SchedulerTimeZoneResponse = {
  id: string
  timezoneId: number
  timezone: string
}

const LAPSE_WINDOW_PRESETS = [14, 21, 30, 60]

const NAV_ITEMS: { id: BusinessTabId; variant: 'business' | 'nudge' | 'voice' | 'schedule' | 'email'; label: string }[] = [
  { id: 'tab-profile',  variant: 'business',  label: 'Profile'     },
  { id: 'tab-nudge',    variant: 'nudge',     label: 'AI Settings' },
  { id: 'tab-voice',    variant: 'voice',     label: 'Brand Voice' },
  { id: 'tab-schedule', variant: 'schedule',  label: 'Schedule'    },
  { id: 'tab-email',    variant: 'email',     label: 'Email'       },
]

const labelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6, display: 'block',
}
const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)',
  borderRadius: 8, padding: '9px 12px', fontSize: 14, color: 'var(--text)',
  outline: 'none', boxSizing: 'border-box',
}
const hintStyle: React.CSSProperties = { fontSize: 12, color: 'var(--text2)', marginTop: 4 }

const readLabelStyle: React.CSSProperties = {
  fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace",
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
}
const readValueStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--text)', lineHeight: 1.5, minHeight: 22,
}
const readEmptyStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--text3)', fontStyle: 'italic',
}

function SectionHeader({
  title, editing, onEdit, onCancel, onSave, saving, saveDisabled,
  saveLabel = 'Save',
}: {
  title: string
  editing: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  saving: boolean
  saveDisabled?: boolean
  saveLabel?: string
}) {
  return (
    <div
      className="settings-section-title"
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <span>{title}</span>
      {!editing && (
        <button
          className="btn-secondary"
          style={{ fontSize: 12, padding: '3px 12px' }}
          onClick={onEdit}
        >
          Edit
        </button>
      )}
      {editing && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" style={{ fontSize: 12, padding: '3px 12px' }} onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="btn-primary" style={{ fontSize: 12, padding: '3px 14px' }} onClick={onSave} disabled={saving || saveDisabled}>
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      )}
    </div>
  )
}

function ReadRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div style={readLabelStyle}>{label}</div>
      {value ? <div style={readValueStyle}>{value}</div> : <div style={readEmptyStyle}>—</div>}
    </div>
  )
}

function SaveFeedback({ error, success, successMsg }: { error: string | null; success: boolean; successMsg: string }) {
  if (error) return (
    <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
      {error}
    </div>
  )
  if (success) return (
    <div style={{ background: '#d1fae5', color: '#065f46', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
      {successMsg}
    </div>
  )
  return null
}

// ─── Profile ──────────────────────────────────────────────────────────────────

function ProfileSection() {
  const { businessInfo, refreshBusinessInfo } = useDashboard()
  const [editing, setEditing] = React.useState(false)

  const [businessName, setBusinessName] = React.useState(businessInfo?.businessName ?? '')
  const [businessType, setBusinessType] = React.useState(businessInfo?.businessType ?? '')
  const [phone, setPhone] = React.useState<PhoneValue | undefined>(businessInfo?.phone as PhoneValue | undefined)
  const [location, setLocation] = React.useState(businessInfo?.location ?? '')
  const [phoneError, setPhoneError] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = React.useState(false)

  React.useEffect(() => {
    if (!businessInfo) return
    setBusinessName(businessInfo.businessName ?? '')
    setBusinessType(businessInfo.businessType ?? '')
    setPhone((businessInfo.phone as PhoneValue | undefined) ?? undefined)
    setLocation(businessInfo.location ?? '')
  }, [businessInfo])

  function reset() {
    if (!businessInfo) return
    setBusinessName(businessInfo.businessName ?? '')
    setBusinessType(businessInfo.businessType ?? '')
    setPhone((businessInfo.phone as PhoneValue | undefined) ?? undefined)
    setLocation(businessInfo.location ?? '')
    setPhoneError(null)
    setSaveError(null)
  }

  function handlePhoneChange(value: PhoneValue | undefined) {
    setPhone(value)
    setPhoneError(value && !isValidPhoneNumber(value) ? 'Enter a valid phone number for the selected country.' : null)
  }

  async function handleSave() {
    if (phone && !isValidPhoneNumber(phone)) { setPhoneError('Enter a valid phone number for the selected country.'); return }
    setSaving(true); setSaveError(null); setSaveSuccess(false)
    try {
      const current = await apiGet<BusinessInfo>(BUSINESS)
      await apiPut<BusinessInfo, Record<string, unknown>>(BUSINESS, {
        ...(current as unknown as Record<string, unknown>),
        businessName, businessType, phone: phone ?? '', location,
      })
      setSaveSuccess(true)
      setEditing(false)
      void refreshBusinessInfo()
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (e) {
      setSaveError((e as { message?: string }).message ?? 'Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="settings-section">
      <SectionHeader
        title="Business Identity"
        editing={editing}
        onEdit={() => setEditing(true)}
        onCancel={() => { reset(); setEditing(false) }}
        onSave={handleSave}
        saving={saving}
        saveDisabled={!!phoneError}
        saveLabel="Save Profile"
      />

      <SaveFeedback error={saveError} success={saveSuccess} successMsg="Profile saved successfully." />

      {!editing ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <ReadRow label="Business Name" value={businessName} />
          <ReadRow label="Business Type" value={businessType} />
          <ReadRow label="Phone" value={phone} />
          <ReadRow label="Location" value={location} />
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Business Name</label>
              <input style={inputStyle} value={businessName} onChange={e => setBusinessName(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Business Type</label>
              <input style={inputStyle} value={businessType} onChange={e => setBusinessType(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Phone</label>
              <PhoneInput
                international defaultCountry="US" value={phone}
                onChange={handlePhoneChange}
                className={phoneError ? 'PhoneInput--error' : undefined}
              />
              {phoneError && <span style={{ ...hintStyle, color: '#ef4444' }}>{phoneError}</span>}
            </div>
            <div>
              <label style={labelStyle}>Location</label>
              <LocationInput style={inputStyle} value={location} onChange={setLocation} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── AI help panel ────────────────────────────────────────────────────────────

const AI_HELP_ITEMS: { title: string; detail: string }[] = [
  { title: 'Offerings catalog', detail: 'The AI references your specific offerings (e.g. "your last balayage" or "your usual latte") to make emails feel personal rather than generic.' },
  { title: 'Visit history',     detail: 'Past visit dates tell the AI exactly how long a customer has been away and what they last did — no invented details.' },
  { title: 'Brand Voice',       detail: 'Your writing style guide shapes every sentence. Warm and casual produces very different emails than formal and professional.' },
  { title: 'Specialty Summary', detail: 'What your business is known for — the AI can use this in the opening hook to immediately signal relevance.' },
  { title: 'Special Offer',     detail: 'A concrete incentive (e.g. "10% off your next visit", "free consultation") gives customers a specific reason to return rather than a vague invitation.' },
  { title: 'Booking URL',       detail: 'Used as the primary call-to-action link in every email. Without it the AI falls back to your phone number.' },
]

function NudgeAIHelpPanel() {
  const [open, setOpen] = React.useState(false)
  return (
    <div style={{ background: 'var(--accent-bg)', border: '1px solid var(--accent-bd)', borderRadius: 10, padding: '12px 16px', marginBottom: 24, fontSize: 13 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={{ fontWeight: 600, color: 'var(--accent)' }}>✦ How your settings improve AI emails</div>
        <span style={{ fontSize: 11, color: 'var(--accent)', userSelect: 'none' }}>{open ? '▲ hide' : '▼ show'}</span>
      </div>
      {open && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {AI_HELP_ITEMS.map(item => (
            <div key={item.title} style={{ display: 'flex', gap: 10 }}>
              <div style={{ minWidth: 140, fontWeight: 500, color: 'var(--text)' }}>{item.title}</div>
              <div style={{ color: 'var(--text2)', lineHeight: 1.5 }}>{item.detail}</div>
            </div>
          ))}
          <div style={{ marginTop: 4, color: 'var(--text2)', fontStyle: 'italic' }}>Fields left blank are simply omitted — the AI never invents missing details.</div>
        </div>
      )}
    </div>
  )
}

// ─── Nudge ────────────────────────────────────────────────────────────────────

function NudgeSection() {
  const { businessInfo, refreshBusinessInfo } = useDashboard()
  const [editing, setEditing] = React.useState(false)

  const [lapseWindowDays, setLapseWindowDays] = React.useState<number>(businessInfo?.lapseWindowDays ?? 30)
  const [lapseCustom, setLapseCustom] = React.useState(!LAPSE_WINDOW_PRESETS.includes(businessInfo?.lapseWindowDays ?? 30))
  const [followUpIntervalDays, setFollowUpIntervalDays] = React.useState<number>(businessInfo?.followUpIntervalDays ?? 3)
  const [emailGenerationInstructions, setEmailGenerationInstructions] = React.useState(businessInfo?.emailGenerationInstructions ?? '')
  const [defaultOfferText, setDefaultOfferText] = React.useState(businessInfo?.defaultOfferText ?? '')
  const [mentionPrices, setMentionPrices] = React.useState(businessInfo?.mentionPrices ?? false)
  const [saving, setSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = React.useState(false)

  React.useEffect(() => {
    if (!businessInfo) return
    const lapse = businessInfo.lapseWindowDays ?? 30
    setLapseWindowDays(lapse)
    setLapseCustom(!LAPSE_WINDOW_PRESETS.includes(lapse))
    setFollowUpIntervalDays(businessInfo.followUpIntervalDays ?? 3)
    setEmailGenerationInstructions(businessInfo.emailGenerationInstructions ?? '')
    setDefaultOfferText(businessInfo.defaultOfferText ?? '')
    setMentionPrices(businessInfo.mentionPrices ?? false)
  }, [businessInfo])

  function reset() {
    if (!businessInfo) return
    const lapse = businessInfo.lapseWindowDays ?? 30
    setLapseWindowDays(lapse)
    setLapseCustom(!LAPSE_WINDOW_PRESETS.includes(lapse))
    setFollowUpIntervalDays(businessInfo.followUpIntervalDays ?? 3)
    setEmailGenerationInstructions(businessInfo.emailGenerationInstructions ?? '')
    setDefaultOfferText(businessInfo.defaultOfferText ?? '')
    setMentionPrices(businessInfo.mentionPrices ?? false)
    setSaveError(null)
  }

  async function handleSave() {
    if (!Number.isInteger(lapseWindowDays) || lapseWindowDays < 14 || lapseWindowDays > 180) {
      setSaveError('Lapse window must be between 14 and 180 days.')
      return
    }
    setSaving(true); setSaveError(null); setSaveSuccess(false)
    try {
      const current = await apiGet<BusinessInfo>(BUSINESS)
      await apiPut<BusinessInfo, Record<string, unknown>>(BUSINESS, {
        ...(current as unknown as Record<string, unknown>),
        lapseWindowDays, followUpIntervalDays, emailGenerationInstructions, defaultOfferText, mentionPrices,
      })
      setSaveSuccess(true)
      setEditing(false)
      void refreshBusinessInfo()
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (e) {
      setSaveError((e as { message?: string }).message ?? 'Failed to save nudge settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="settings-section">
      <SectionHeader
        title="Nudge Configuration"
        editing={editing}
        onEdit={() => setEditing(true)}
        onCancel={() => { reset(); setEditing(false) }}
        onSave={handleSave}
        saving={saving}
        saveLabel="Save Settings"
      />

      <SaveFeedback error={saveError} success={saveSuccess} successMsg="Nudge settings saved successfully." />

      {/* Max Sequence — always visible */}
      <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent)', minWidth: 28, textAlign: 'center' }}>3</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Max Sequence Emails</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Nudge sends up to 3 AI-generated emails per lapsed contact. This is fixed and cannot be changed.</div>
        </div>
      </div>

      {!editing ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <ReadRow label="Lapse Window" value={`${lapseWindowDays} days`} />
          <ReadRow label="Follow-up Interval" value={`${followUpIntervalDays} days between emails`} />
          <div style={{ gridColumn: '1 / -1' }}>
            <ReadRow label="Email Generation Instructions" value={emailGenerationInstructions || undefined} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <ReadRow label="Special Offer" value={defaultOfferText || undefined} />
          </div>
          <div>
            <div style={readLabelStyle}>Show Prices in Emails</div>
            <div style={readValueStyle}>{mentionPrices ? 'Enabled' : 'Disabled'}</div>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Lapse Window</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              {LAPSE_WINDOW_PRESETS.map(days => (
                <button key={days} onClick={() => { setLapseWindowDays(days); setLapseCustom(false) }}
                  style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, cursor: 'pointer', background: !lapseCustom && lapseWindowDays === days ? 'var(--accent)' : 'var(--surface2)', color: !lapseCustom && lapseWindowDays === days ? '#fff' : 'var(--text)', fontWeight: !lapseCustom && lapseWindowDays === days ? 600 : 400 }}>
                  {days} days
                </button>
              ))}
              <button onClick={() => setLapseCustom(true)}
                style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, cursor: 'pointer', background: lapseCustom ? 'var(--accent)' : 'var(--surface2)', color: lapseCustom ? '#fff' : 'var(--text)', fontWeight: lapseCustom ? 600 : 400 }}>
                Custom
              </button>
            </div>
            {lapseCustom && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="number" min={14} max={180} value={lapseWindowDays} onChange={e => setLapseWindowDays(Number(e.target.value))} style={{ ...inputStyle, width: 100 }} />
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>days (min 14)</span>
              </div>
            )}
            <div style={hintStyle}>
              A contact is considered lapsed after this many days without a visit.
              Windows shorter than 14 days tend to over-message customers and lower reply rates — we recommend 21–30 days for most businesses.
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Follow-up Interval</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="number" min={1} max={30} value={followUpIntervalDays} onChange={e => setFollowUpIntervalDays(Number(e.target.value))} style={{ ...inputStyle, width: 100 }} />
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>days between follow-up emails</span>
            </div>
            <div style={hintStyle}>How many days to wait before sending the next email in the sequence.</div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Email Generation Instructions</label>
            <textarea rows={5} value={emailGenerationInstructions} onChange={e => setEmailGenerationInstructions(e.target.value)} placeholder="e.g. Keep it warm and casual. Always mention our loyalty program. Sign off from Sarah. Never push for a discount." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
            <div style={hintStyle}>Plain-English instructions the AI follows when writing your nudge emails. Leave blank to let the AI decide on its own.</div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Special Offer</label>
            <textarea rows={3} value={defaultOfferText} onChange={e => setDefaultOfferText(e.target.value)} placeholder="e.g. 10% off your next visit, free consultation, complimentary add-on..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
            <div style={hintStyle}>The AI will weave this into emails when it makes sense — leave blank for no offer.</div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle}>Show Prices in Emails</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="mention-prices" checked={mentionPrices} onChange={e => setMentionPrices(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
              <label htmlFor="mention-prices" style={{ fontSize: 14, color: 'var(--text)', cursor: 'pointer' }}>Allow AI to mention offering prices in emails</label>
            </div>
            <div style={hintStyle}><strong style={{ color: 'var(--red, #dc2626)' }}>Risk:</strong> Only enable if your prices are stable. Incorrect prices in emails erode trust. Prices are only shown if they appear in your offerings catalog.</div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Brand Voice ──────────────────────────────────────────────────────────────

function BrandVoiceSection() {
  const { businessInfo, refreshBusinessInfo } = useDashboard()
  const [editing, setEditing] = React.useState(false)

  const [brandVoice, setBrandVoice] = React.useState(businessInfo?.brandVoice ?? '')
  const [specialtySummary, setSpecialtySummary] = React.useState(businessInfo?.specialtySummary ?? '')
  const [bookingUrl, setBookingUrl] = React.useState(businessInfo?.bookingUrl ?? '')
  const [hoursSummary, setHoursSummary] = React.useState(businessInfo?.hoursSummary ?? '')
  const [websiteUrl, setWebsiteUrl] = React.useState(businessInfo?.websiteUrl ?? '')
  const [saving, setSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = React.useState(false)

  React.useEffect(() => {
    if (!businessInfo) return
    setBrandVoice(businessInfo.brandVoice ?? '')
    setSpecialtySummary(businessInfo.specialtySummary ?? '')
    setBookingUrl(businessInfo.bookingUrl ?? '')
    setHoursSummary(businessInfo.hoursSummary ?? '')
    setWebsiteUrl(businessInfo.websiteUrl ?? '')
  }, [businessInfo])

  function reset() {
    if (!businessInfo) return
    setBrandVoice(businessInfo.brandVoice ?? '')
    setSpecialtySummary(businessInfo.specialtySummary ?? '')
    setBookingUrl(businessInfo.bookingUrl ?? '')
    setHoursSummary(businessInfo.hoursSummary ?? '')
    setWebsiteUrl(businessInfo.websiteUrl ?? '')
    setSaveError(null)
  }

  async function handleSave() {
    setSaving(true); setSaveError(null); setSaveSuccess(false)
    try {
      const current = await apiGet<BusinessInfo>(BUSINESS)
      await apiPut<BusinessInfo, Record<string, unknown>>(BUSINESS, {
        ...(current as unknown as Record<string, unknown>),
        brandVoice: brandVoice || null, specialtySummary: specialtySummary || null,
        bookingUrl: bookingUrl || null, hoursSummary: hoursSummary || null, websiteUrl: websiteUrl || null,
      })
      setSaveSuccess(true)
      setEditing(false)
      void refreshBusinessInfo()
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (e) {
      setSaveError((e as { message?: string }).message ?? 'Failed to save brand voice settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="settings-section">
      <SectionHeader
        title="Brand Voice"
        editing={editing}
        onEdit={() => setEditing(true)}
        onCancel={() => { reset(); setEditing(false) }}
        onSave={handleSave}
        saving={saving}
        saveLabel="Save Brand Voice"
      />

      <SaveFeedback error={saveError} success={saveSuccess} successMsg="Brand voice saved successfully." />

      {!editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <ReadRow label="Writing Style" value={brandVoice || undefined} />
          <ReadRow label="Specialty Summary" value={specialtySummary || undefined} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <ReadRow label="Booking URL" value={bookingUrl || undefined} />
            <ReadRow label="Website URL" value={websiteUrl || undefined} />
          </div>
          <ReadRow label="Business Hours" value={hoursSummary || undefined} />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Writing Style</label>
            <textarea rows={4} value={brandVoice} maxLength={500} onChange={e => setBrandVoice(e.target.value)} placeholder="e.g. warm and conversational, always use the customer's first name, avoid formal language, end with an upbeat sign-off..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
            <div style={{ ...hintStyle, display: 'flex', justifyContent: 'space-between' }}>
              <span>Describe the tone the AI should use when writing emails on your behalf.</span>
              <span style={{ color: brandVoice.length > 450 ? 'var(--red, #dc2626)' : 'var(--text2)' }}>{brandVoice.length}/500</span>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Specialty Summary</label>
            <textarea rows={3} value={specialtySummary} maxLength={300} onChange={e => setSpecialtySummary(e.target.value)} placeholder="e.g. precision haircuts, balayage, keratin treatments, bridal packages..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
            <div style={{ ...hintStyle, display: 'flex', justifyContent: 'space-between' }}>
              <span>What your business is known for — the AI can reference this to make emails feel specific.</span>
              <span style={{ color: specialtySummary.length > 270 ? 'var(--red, #dc2626)' : 'var(--text2)' }}>{specialtySummary.length}/300</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Booking URL</label>
              <input type="url" value={bookingUrl} maxLength={500} onChange={e => setBookingUrl(e.target.value)} placeholder="https://book.yoursalon.com" style={inputStyle} />
              <div style={hintStyle}>Included as the primary call-to-action link in emails.</div>
            </div>
            <div>
              <label style={labelStyle}>Website URL</label>
              <input type="url" value={websiteUrl} maxLength={500} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://yoursalon.com" style={inputStyle} />
              <div style={hintStyle}>Fallback link used when no booking URL is set.</div>
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle}>Business Hours</label>
            <input type="text" value={hoursSummary} maxLength={500} onChange={e => setHoursSummary(e.target.value)} placeholder="e.g. Mon–Fri 9am–7pm, Sat 10am–5pm, closed Sunday" style={inputStyle} />
            <div style={hintStyle}>The AI can mention your hours when customers ask or when it adds context to the email.</div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Schedule ─────────────────────────────────────────────────────────────────

function ScheduleSection() {
  const { businessInfo, refreshBusinessInfo } = useDashboard()
  const [editing, setEditing] = React.useState(false)

  const [timezone, setTimezone] = React.useState(businessInfo?.timezone ?? '')
  const [selectedSlotId, setSelectedSlotId] = React.useState(
    businessInfo?.schedulerSlotId != null ? String(businessInfo.schedulerSlotId) : '',
  )
  const [availableTimezones, setAvailableTimezones] = React.useState<SchedulerTimeZoneResponse[]>([])
  const [timezonesLoading, setTimezonesLoading] = React.useState(false)
  const [timezonesError, setTimezonesError] = React.useState<string | null>(null)
  const [schedulerSlots, setSchedulerSlots] = React.useState<SchedulerSlotResponse[]>([])
  const [slotsLoading, setSlotsLoading] = React.useState(false)
  const [slotsError, setSlotsError] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = React.useState(false)

  React.useEffect(() => {
    if (!businessInfo) return
    if (businessInfo.timezone) setTimezone(businessInfo.timezone)
    if (businessInfo.schedulerSlotId != null) setSelectedSlotId(String(businessInfo.schedulerSlotId))
  }, [businessInfo])

  React.useEffect(() => {
    if (!editing) return
    let cancelled = false
    setTimezonesLoading(true); setTimezonesError(null)
    apiGet<SchedulerTimeZoneResponse[]>(BUSINESS_TIMEZONES)
      .then(zones => { if (!cancelled) setAvailableTimezones(zones) })
      .catch(e => { if (!cancelled) setTimezonesError((e as { message?: string }).message ?? 'Failed to load timezones') })
      .finally(() => { if (!cancelled) setTimezonesLoading(false) })
    return () => { cancelled = true }
  }, [editing])

  React.useEffect(() => {
    if (!editing || !timezone.trim()) return
    let cancelled = false
    setSlotsLoading(true); setSlotsError(null)
    apiGet<SchedulerSlotResponse[]>(BUSINESS_SCHEDULER_SLOTS(timezone))
      .then(slots => {
        if (cancelled) return
        setSchedulerSlots(slots)
        setSelectedSlotId(prev => slots.some(s => s.slotId === prev) ? prev : (slots[0]?.slotId ?? ''))
      })
      .catch(e => { if (cancelled) return; setSchedulerSlots([]); setSlotsError((e as { message?: string }).message ?? 'Failed to load slots') })
      .finally(() => { if (!cancelled) setSlotsLoading(false) })
    return () => { cancelled = true }
  }, [editing, timezone])

  function reset() {
    if (!businessInfo) return
    if (businessInfo.timezone) setTimezone(businessInfo.timezone)
    if (businessInfo.schedulerSlotId != null) setSelectedSlotId(String(businessInfo.schedulerSlotId))
    setSaveError(null)
  }

  async function handleSave() {
    setSaving(true); setSaveError(null); setSaveSuccess(false)
    try {
      const current = await apiGet<BusinessInfo>(BUSINESS)
      const slot = schedulerSlots.find(s => s.slotId === selectedSlotId)
      await apiPut<BusinessInfo, Record<string, unknown>>(BUSINESS, {
        ...(current as unknown as Record<string, unknown>),
        timezone, preferredSlotId: selectedSlotId,
        dailyScanTime: slot?.fireTimeLocal ?? (current.dailyScanTime ?? ''),
      })
      setSaveSuccess(true)
      setEditing(false)
      void refreshBusinessInfo()
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (e) {
      setSaveError((e as { message?: string }).message ?? 'Failed to save schedule')
    } finally {
      setSaving(false)
    }
  }

  const selectShellStyle: React.CSSProperties = { position: 'relative', display: 'block' }
  const selectStyle: React.CSSProperties = {
    width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '9px 36px 9px 12px', fontSize: 14, color: 'var(--text)',
    outline: 'none', appearance: 'none', cursor: 'pointer',
  }
  const chevronStyle: React.CSSProperties = {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    pointerEvents: 'none', fontSize: 11, color: 'var(--text2)',
  }

  const currentSlot = schedulerSlots.find(s => s.slotId === selectedSlotId)

  return (
    <div className="settings-section">
      <SectionHeader
        title="Scan Schedule"
        editing={editing}
        onEdit={() => setEditing(true)}
        onCancel={() => { reset(); setEditing(false) }}
        onSave={handleSave}
        saving={saving}
        saveDisabled={!selectedSlotId || slotsLoading}
        saveLabel="Save Schedule"
      />

      <SaveFeedback error={saveError} success={saveSuccess} successMsg="Schedule saved successfully." />

      {!editing ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <ReadRow label="Timezone" value={timezone || undefined} />
          <ReadRow
            label="Send Time"
            value={businessInfo?.dailyScanTime ? `${businessInfo.dailyScanTime.slice(0, 5)} ${timezone}` : undefined}
          />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Timezone</label>
            <div style={selectShellStyle}>
              <select value={timezone} onChange={e => setTimezone(e.target.value)} disabled={timezonesLoading} style={selectStyle}>
                {timezonesLoading && <option>Loading timezones…</option>}
                {!timezonesLoading && availableTimezones.length === 0 && <option value="">No timezones available</option>}
                {!timezonesLoading && availableTimezones.map(zone => (
                  <option key={zone.id} value={zone.timezone}>{zone.timezone}</option>
                ))}
              </select>
              <span style={chevronStyle}>▼</span>
            </div>
            {timezonesError && <div style={{ color: 'var(--red, #dc2626)', fontSize: 12, marginTop: 4 }}>{timezonesError}</div>}
            <div style={hintStyle}>Your selected timezone determines when the automated nudge scan runs.</div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle}>Preferred Send Time</label>
            <div style={selectShellStyle}>
              <select value={selectedSlotId} onChange={e => setSelectedSlotId(e.target.value)} disabled={slotsLoading || schedulerSlots.length === 0} style={selectStyle}>
                {slotsLoading && <option>Loading available slots…</option>}
                {!slotsLoading && schedulerSlots.length === 0 && <option value="">No slots available for this timezone</option>}
                {!slotsLoading && schedulerSlots.map(slot => (
                  <option key={slot.slotId} value={slot.slotId}>{slot.slotCode.replace('SLOT_', '')} — {slot.fireTimeLocal.slice(0, 5)}</option>
                ))}
              </select>
              <span style={chevronStyle}>▼</span>
            </div>
            {slotsError && <div style={{ color: 'var(--red, #dc2626)', fontSize: 12, marginTop: 4 }}>{slotsError}</div>}
            {currentSlot && <div style={hintStyle}>Nudge scans will run at <strong>{currentSlot.fireTimeLocal.slice(0, 5)}</strong> {timezone}.</div>}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Schedule gate ────────────────────────────────────────────────────────────

function ScheduleGate({
  onGoProfile,
  onGoAISettings,
  children,
}: {
  onGoProfile: () => void
  onGoAISettings: () => void
  children: React.ReactNode
}) {
  const { businessInfo } = useDashboard()

  const missingType  = !businessInfo?.businessType?.trim()
  const missingLapse = businessInfo?.lapseWindowDays == null

  if (businessInfo && !missingType && !missingLapse) return <>{children}</>

  if (!businessInfo) return null

  return (
    <div className="settings-section">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '48px 24px',
          textAlign: 'center',
          gap: 16,
        }}
      >
        <svg
          width="32" height="32" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
          style={{ color: 'var(--text3)', opacity: 0.6 }}
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
          Complete your profile to unlock the scheduler
        </div>
        <div style={{ fontSize: 13, color: 'var(--text2)', maxWidth: 360, lineHeight: 1.6 }}>
          Nudge needs a few more details before automated email scanning can be configured.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 280 }}>
          {missingType && (
            <button
              className="btn-secondary"
              style={{ fontSize: 13, padding: '8px 16px', width: '100%' }}
              onClick={onGoProfile}
            >
              Add business type — Profile tab
            </button>
          )}
          {missingLapse && (
            <button
              className="btn-secondary"
              style={{ fontSize: 13, padding: '8px 16px', width: '100%' }}
              onClick={onGoAISettings}
            >
              Set lapse window — AI Settings tab
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Email ────────────────────────────────────────────────────────────────────

function EmailSection() {
  const { businessInfo, refreshBusinessInfo } = useDashboard()
  const [editing, setEditing] = React.useState(false)

  const [fromDisplayName, setFromDisplayName] = React.useState(
    businessInfo?.fromDisplayName ?? businessInfo?.businessName ?? '',
  )
  const [saving, setSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = React.useState(false)

  React.useEffect(() => {
    if (!businessInfo) return
    setFromDisplayName(businessInfo.fromDisplayName ?? businessInfo.businessName ?? '')
  }, [businessInfo])

  function reset() {
    if (!businessInfo) return
    setFromDisplayName(businessInfo.fromDisplayName ?? businessInfo.businessName ?? '')
    setSaveError(null)
  }

  async function handleSave() {
    setSaving(true); setSaveError(null); setSaveSuccess(false)
    try {
      const current = await apiGet<BusinessInfo>(BUSINESS)
      await apiPut<BusinessInfo, Record<string, unknown>>(BUSINESS, {
        ...(current as unknown as Record<string, unknown>), fromDisplayName,
      })
      setSaveSuccess(true)
      setEditing(false)
      void refreshBusinessInfo()
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (e) {
      setSaveError((e as { message?: string }).message ?? 'Failed to save email settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="settings-section">
        <SectionHeader
          title="Sending Configuration"
          editing={editing}
          onEdit={() => setEditing(true)}
          onCancel={() => { reset(); setEditing(false) }}
          onSave={handleSave}
          saving={saving}
          saveDisabled={!fromDisplayName.trim()}
          saveLabel="Save Email Settings"
        />

        <SaveFeedback error={saveError} success={saveSuccess} successMsg="Email settings saved successfully." />

        <div style={{ background: 'var(--accent-bg)', border: '1px solid var(--accent-bd)', borderRadius: 10, padding: '14px 16px', marginBottom: 20, fontSize: 13, color: 'var(--text2)' }}>
          <strong style={{ color: 'var(--accent)' }}>Active</strong> — Nudge emails are sent from{' '}
          <code style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, background: 'var(--surface2)', padding: '1px 5px', borderRadius: 4 }}>
            {businessInfo?.sendingEmail ?? 'nudge@usenudge.io'}
          </code>
          . Customer replies are routed back through Nudge automatically.
        </div>

        {!editing ? (
          <ReadRow label="From Display Name" value={fromDisplayName || undefined} />
        ) : (
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle}>From Display Name — <span style={{ fontWeight: 400, color: 'var(--text2)' }}>email sender name</span></label>
            <input style={inputStyle} value={fromDisplayName} onChange={e => setFromDisplayName(e.target.value)} />
            <div style={hintStyle}>Shown as the sender name in customer emails — e.g. &quot;Joe&apos;s Salon&quot;</div>
          </div>
        )}
      </div>

      <div className="settings-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div className="settings-section-title" style={{ margin: 0 }}>Custom Domain</div>
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: 'var(--text2)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 7px' }}>COMING SOON</span>
        </div>
        <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }}>
          <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 6, fontWeight: 500 }}>Send from your own domain</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>Add DNS records to send from your own domain — no &quot;via Nudge&quot; label.</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', fontStyle: 'italic' }}>This feature is on our roadmap and will be available in a future update.</div>
        </div>
      </div>
    </>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function BusinessPage({ isActive }: { isActive: boolean }) {
  const { me, businessInfo, refreshBusinessInfo } = useDashboard()
  const [activeTab, setActiveTab] = React.useState<BusinessTabId>('tab-profile')
  const [logoUploading, setLogoUploading] = React.useState(false)
  const [logoError, setLogoError] = React.useState<string | null>(null)
  const [logoCropSrc, setLogoCropSrc] = React.useState<string | null>(null)

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    e.target.value = ''
    setLogoError(null)
    setLogoCropSrc(URL.createObjectURL(f))
  }

  function handleLogoCropConfirm(blob: Blob) {
    const file = new File([blob], 'logo.jpg', { type: 'image/jpeg' })
    if (logoCropSrc) { URL.revokeObjectURL(logoCropSrc); setLogoCropSrc(null) }
    setLogoUploading(true)
    uploadToCloudinary(file, 'business-logos')
      .then(({ secureUrl }) =>
        apiPut<void, { logoUrl: string }>(BUSINESS_LOGO, { logoUrl: secureUrl })
          .then(() => refreshBusinessInfo())
      )
      .catch(err => setLogoError((err as Error).message ?? 'Logo upload failed'))
      .finally(() => setLogoUploading(false))
  }

  return (
    <PageShell id="page-business" isActive={isActive}>
      {logoCropSrc && (
        <ImageCropModal
          srcUrl={logoCropSrc}
          variant="logo"
          onConfirm={handleLogoCropConfirm}
          onCancel={() => { URL.revokeObjectURL(logoCropSrc); setLogoCropSrc(null) }}
        />
      )}
      {logoError && (
        <div style={{ marginBottom: 12, background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--red)', padding: '10px 14px', borderRadius: 10, fontSize: 13 }}>
          {logoError}
        </div>
      )}
      <div className="profile-hero">
        <div className="profile-avatar-wrap">
          <div
            className="profile-avatar"
            onClick={() => !logoUploading && document.getElementById('logoInput')?.click()}
            style={{
              ...(businessInfo?.logoUrl
                ? { backgroundImage: `url(${businessInfo.logoUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', color: 'transparent' }
                : undefined),
              cursor: logoUploading ? 'wait' : 'pointer',
              opacity: logoUploading ? 0.6 : 1,
            }}
          >
            {businessInfo?.logoUrl ? null : '🏢'}
            <div className="avatar-edit-badge" title={logoUploading ? 'Uploading…' : 'Change logo'}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {logoUploading
                  ? <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" />
                  : <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>
                }
              </svg>
            </div>
          </div>
          <input
            type="file"
            id="logoInput"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleLogoChange}
          />
        </div>
        <div className="profile-hero-body">
          {businessInfo === null ? (
            <>
              <div className="profile-hero-title">
                <Skeleton height={18} width={180} />
                <Skeleton height={12} width={160} />
              </div>
              <div className="profile-hero-info" aria-hidden="true">
                <Skeleton height={18} width={72} borderRadius={99} />
                <Skeleton height={18} width={88} borderRadius={99} />
                <Skeleton height={18} width={100} borderRadius={99} />
                <div className="profile-meta-sep" />
                <Skeleton height={12} width={80} />
                <Skeleton height={12} width={100} />
              </div>
            </>
          ) : (
            <>
              <div className="profile-hero-title">
                <span className="profile-name">{businessInfo.businessName || '—'}</span>
                <span className="profile-role">{businessInfo.businessType || 'Business'} · Business Configuration</span>
              </div>
              <div className="profile-hero-info">
                <span className="profile-badge pb-plan">✦ Free Plan</span>
                <span className="profile-badge pb-verified">✓ Domain verified</span>
                <span className="profile-badge pb-member">{formatLiveSince(me?.createdAt)}</span>
                <div className="profile-meta-sep" />
                <div className="profile-meta-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8 10a16 16 0 0 0 6 6l1.36-1.36a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  {businessInfo.phone || '—'}
                </div>
                <div className="profile-meta-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {businessInfo.location || '—'}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="settings-tabbar" role="tablist" aria-label="Business sections">
        {NAV_ITEMS.map(({ id, variant, label }) => (
          <button key={id} role="tab" aria-selected={activeTab === id}
            className={`settings-tabbar-item${activeTab === id ? ' active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <SettingsTabButtonIcon variant={variant} />
            {label}
          </button>
        ))}
      </div>

      <div className="content">
        {activeTab === 'tab-profile'  && <div className="settings-panel" role="tabpanel"><ProfileSection /></div>}
        {activeTab === 'tab-nudge'    && <div className="settings-panel" role="tabpanel"><NudgeAIHelpPanel /><NudgeSection /></div>}
        {activeTab === 'tab-voice'    && <div className="settings-panel" role="tabpanel"><BrandVoiceSection /></div>}
        {activeTab === 'tab-schedule' && (
          <div className="settings-panel" role="tabpanel">
            <ScheduleGate
              onGoProfile={() => setActiveTab('tab-profile')}
              onGoAISettings={() => setActiveTab('tab-nudge')}
            >
              <ScheduleSection />
            </ScheduleGate>
          </div>
        )}
        {activeTab === 'tab-email'    && <div className="settings-panel" role="tabpanel"><EmailSection /></div>}
      </div>
    </PageShell>
  )
}
